import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  OomPahPlayer,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay5() {
  const { lesson, course, accent } = findLesson("sing-and-play", "5");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/sing-and-play/SP5-Oom-Pah-Sunny-Side-Animated.mp4"
        caption={`Walking bass — LH connects chord roots with passing notes`}
        hint="≈ 26 sec · bass tells a story"
      />
      {/* ── 1. Context ─────────────────────────────────────────────────── */}
      <SAPSection eyebrow="The big idea" title="Keep on the Sunny Side — colour inside a major key">
        <p>
          You know I–IV–V in three keys now. You know the secondary dominant. You know quick
          changes and slow changes and how the left hand is the engine. This lesson takes all of
          that and adds one new thing: a moment of <strong>shadow</strong> inside a bright song.
        </p>
        <p>
          Keep on the Sunny Side is unabashedly cheerful — the Carter Family's famous hymn-like
          anthem. But inside the chorus, there's a brief Em chord (the vi, the relative minor of
          G major). It arrives for just a bar, and it changes the emotional weight of the whole
          phrase. It's the dark corner in a bright room. And understanding why it works is your
          first real lesson in how major songs borrow colour from the minor world.
        </p>
        <SAPQuote>
          So it's very much one, four, five chords, but the changes are interesting.
        </SAPQuote>
      </SAPSection>

      {/* ── 2. Reference recording ─────────────────────────────────────── */}
      <SAPSection eyebrow="Listen first" title="The Carter Family — Keep on the Sunny Side">
        <p>
          This is the Carter Family's signature song — written in 1899, recorded in 1928, and
          still sung in churches and parlours. Listen to how bright and forward-moving the
          Oom-Pah groove feels. Then listen for the moment the mood shifts slightly in the
          chorus — that's the vi chord doing its work.
        </p>
        <YouTubeEmbed
          videoId="UrI_ZAkgHBI"
          title="The Carter Family — Keep on the Sunny Side"
          caption="The Carter Family's signature recording, 1928. Listen for the brief minor colour in the chorus."
        />
      </SAPSection>

      {/* ── 3. The chords ──────────────────────────────────────────────── */}
      <SAPSection eyebrow="The harmony" title="G major with Em and a passing G7">
        <p>
          G major is the home. C (IV) and D7 (V7) are the two supporting chords, same as Oh
          Susanna. But this song adds two colours:
        </p>
        <p>
          First, <strong>Em (vi)</strong> — the relative minor. Em shares two notes with G major
          (G and B), so it doesn't feel like a foreign chord. It feels like G with a sad tilt.
          When it appears in the chorus, it creates a brief emotional shadow before G returns.
          This is your introduction to the concept of <em>borrowed relative minor</em>.
        </p>
        <p>
          Second, <strong>G7</strong> — the tonic chord with a minor 7th (F natural). In G
          major, a G7 is technically the I chord acting as V of the IV. It's a passing chord,
          not a cadential dominant. It appears naturally right before a C chord, making the
          move to C feel smooth and inevitable.
        </p>

        <SAPChordRow
          label="Full chord vocabulary · G major"
          chords={[
            { label: "G",  rn: "I",   pitches: ["G3","B3","D4","G4"],  fn: "tonic" },
            { label: "C",  rn: "IV",  pitches: ["C4","E4","G4","C5"],  fn: "subdominant" },
            { label: "D7", rn: "V7",  pitches: ["D3","F#3","A3","C4"], fn: "dominant" },
            { label: "Em", rn: "vi",  pitches: ["E3","G3","B3","E4"],  fn: "submediant" },
            { label: "G7", rn: "I7",  pitches: ["G3","B3","D4","F4"],  fn: "special" },
          ]}
          caption="Em (vi) is the relative minor — the dark corner. G7 is a passing chord that leads naturally to C."
        />

        <RomanNumeralAnalysis
          label="Chorus · G major with relative minor"
          caption="The Em arrives unexpectedly — same key, minor quality. It makes G's return feel like the sun coming out."
          chords={[
            { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { chord: "G7", rn: "I7",  function: "Secondary V",  pitches: ["G3","B3","D4","F4"] },
            { bar: true },
            { chord: "C",  rn: "IV",  function: "Pre-dominant", pitches: ["C4","E4","G4","C5"] },
            { bar: true },
            { chord: "Em", rn: "vi",  function: "Submediant",      pitches: ["E3","G3","B3","E4"] },
            { bar: true },
            { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { bar: true },
            { chord: "D7", rn: "V7",  function: "Dominant",     pitches: ["D3","F#3","A3","C4"] },
            { bar: true },
            { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
          ]}
        />
      </SAPSection>

      {/* ── 4. Interactive loop ────────────────────────────────────────── */}
      <SAPSection eyebrow="Interactive" title="The Oom-Pah loop — G major with Em colour">
        <p>
          This loop includes the Em bar so you can feel the minor shadow in context. Notice how
          the amber bass line under Em sits on E instead of G — that single note change is what
          tells your ear "something shifted here." Listen for it.
        </p>
        <OomPahPlayer
          progression={[
            { label: "G",  bars: 2, lhRoot: "G3", lhFifth: "D4", rh: ["G4","B4","D5"] },
            { label: "G7", bars: 1, lhRoot: "G3", lhFifth: "D4", rh: ["B4","D5","F5"] },
            { label: "C",  bars: 1, lhRoot: "C4", lhFifth: "G4", rh: ["E4","G4","C5"] },
            { label: "Em", bars: 1, lhRoot: "E3", lhFifth: "B3", rh: ["E4","G4","B4"] },
            { label: "G",  bars: 1, lhRoot: "G3", lhFifth: "D4", rh: ["G4","B4","D5"] },
          ]}
          bpm={86}
          label="KEEP ON THE SUNNY SIDE — G major · 6-bar loop with Em"
        />
        <SAPCallout label="The Em moment" variant="insight">
          Stop the loop right on the Em bar and hold it. Play just Em in both hands — bass E,
          chord E–G–B. Listen to how it feels compared to G. It's not sad exactly — it's just
          quieter, more inward. The sun went behind a cloud. Now release to G and hear the
          sun come back. That's what harmonic colour means.
        </SAPCallout>
      </SAPSection>

      {/* ── 5. The borrowed relative minor ─────────────────────────────── */}
      <SAPSection eyebrow="Music theory" title="Why Em lives inside G major">
        <p>
          Em isn't borrowed from outside the key — it's native to G major. The G major scale
          contains G–A–B–C–D–E–F♯. Build a chord on E using only those notes: E–G–B. That's
          Em. It's the <strong>vi chord</strong>, built on the 6th degree of the scale.
        </p>
        <p>
          We call Em the "relative minor" of G major because it shares the same notes, just
          starting from a different root. Songs use it when they want minor emotional colour
          without leaving the key. It's not a modulation — it's a darker room in the same house.
        </p>
        <SAPCallout label="The relative minor rule" variant="insight">
          In any major key, the relative minor is built on the 6th scale degree. G major →
          Em. C major → Am. D major → Bm. F major → Dm. The vi chord is always available,
          always native to the key, always the darker option. Remember that.
        </SAPCallout>

      </SAPSection>

      {/* ── 6. Putting it together ─────────────────────────────────────── */}
      <SAPSection eyebrow="Synthesis" title="Five songs, one pattern — what you've built">
        <p>
          You've now played Oom-Pah through five folk and gospel songs across multiple keys.
          Each one taught you something specific: I'll Fly Away gave you the pattern itself.
          Oh Susanna gave you quick changes. You Are My Sunshine gave you hand independence.
          In the Highways gave you the secondary dominant. And Keep on the Sunny Side gives
          you the relative minor and harmonic colour.
        </p>
        <p>
          The Oom-Pah pattern stays the same across these songs. The songs are different because harmony is
          different — different relationships between the chords, different emotional arcs. The
          Oom-Pah is the vehicle. The harmony is the journey.
        </p>
        <SAPCallout label="Take it further" variant="tip">
          Try transposing Keep on the Sunny Side to D major. Your new vi chord will be Bm.
          The pattern stays identical; only the pitch names change. If you can play it in G
          and D, try C. Three keys, same song — that's transposition in practice.
        </SAPCallout>
      </SAPSection>

      {/* ── 7. Practice plan ───────────────────────────────────────────── */}

      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord chart" title="Handwritten teaching notes">
        <p>
          This is Jason's original handwritten chord chart for this song — the same one he
          uses in the video lesson. Click to zoom in and follow along.
        </p>
        <NoteImage
          src="/notes/sing-and-play/05-keep-on-the-sunny-side.png"
          caption="Jason's chord chart — Keep on the Sunny Side in B♭ major"
        />
      </SAPSection>
      <SAPSection eyebrow="Practice plan" title="Groove, colour, and singing">
        <SAPPracticeLadder
          intro="This lesson consolidates everything. Work through it slowly — there's no new technique, just new harmony. Let your ears lead."
          rungs={[
            {
              title: "G–C–D7 without Em",
              minutes: 5,
              detail: "Play the plain I–IV–V Oom-Pah in G. Three chords, steady groove, 72 bpm. This is your baseline.",
              accent: "amber",
            },
            {
              title: "Insert G7 before C",
              minutes: 4,
              detail: "G → G7 → C. Feel how the F natural in G7 creates forward pull into C. One extra note, one new feeling.",
              accent: "amber",
            },
            {
              title: "Insert Em in the chorus",
              minutes: 6,
              detail: "Play the full chorus chord sequence with Em. Stop on Em and listen. Then release to G. Own that moment.",
              accent: "purple",
            },
            {
              title: "Full verse + chorus Oom-Pah",
              minutes: 8,
              detail: "Both hands, all chords, 80 bpm. Verse and chorus back to back. The pattern stays locked through all changes.",
              accent: "purple",
            },
            {
              title: "Sing the full song",
              minutes: 12,
              detail: "Add the melody. Keep your eyes up. Trust the engine. Let the Em bar surprise you a little every time — that's the point.",
              accent: "emerald",
            },
          ]}
        />
      </SAPSection>
    </LessonShell>
  );
}
